import _ from 'lodash';
import {
  useEffect,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import {useGetViewingSession} from '../../../hooks/document/useDocument';
import {
  generateTimestampId,
  injectCss,
  injectScript,
  mapKeywordOccurrences,
  MouseToolType,
  normalizeString,
  remarksColors,
  searchHighlightColor,
} from '../../document-viewer/docViewerUtils';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {ToastType} from '../../../shared/constants';
import {t} from 'i18next';
import {DocViewKeyword, DocumentFilePath} from '../../../api/types/document.type';

const DEBOUNCE_TIMER = 100;

interface SearchQueryItem {
  highlightColor: string;
  searchTerm: string;
  searchTermIsRegex: boolean;
  contextPadding: number;
  matchingOptions?: {
    exactPhrase: boolean;
    matchCase: boolean;
    matchWholeWord: boolean;
    beginsWith: boolean;
    endsWith: boolean;
    wildcard: boolean;
  };
}

const ViewerConfig = {
  assetsBase: '/viewer-assets',
  proxyBase: '/pas',
};

interface ClickEvent {
  clientX: number;
  clientY: number;
  mark: Mark;
  targetType: string;
}

interface RemoveEvent {
  mark: Mark;
  type: string;
}

export interface DocumentPathType {
  documentId: number;
  documentPath: string;
  documentExt: string;
  documentFilePaths?: DocumentFilePath[];
}

export interface SelectedMarkPosition {
  x: number;
  y: number;
  mark: Mark;
}

export type ResultEntry = {
  count: number;
  colorCode: string;
  hits: SearchResult[];
  name: string;
  type: string;
  actualSearchString: string;
};

export function usePrizmDocAnnotations(
  documentparams: DocumentPathType | null,
  containerRef: React.RefObject<HTMLDivElement | null>,
  thumbContainerRef?: React.RefObject<HTMLDivElement | null>,
  useAnnotations?: boolean,
  setViewerReady?: React.Dispatch<React.SetStateAction<boolean>>,
  documentIdsToDisableControls = [] as string[],
  annotationLayerId?: string,
  connectionId?: string,
  rightPanelRef?: React.RefObject<HTMLDivElement | null>,
  setRedactionMarkId?: Dispatch<
    SetStateAction<{id: string; redactionId: string; mark: Mark} | null>
  >,
  onRedactionSaved?: () => void,
  selectionMode?: string,
  redactionPropagationEnabled?: boolean,
  triggerToast?: (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => void,
  keywordsHighlight?: DocViewKeyword[],
  currentRemarkColor?: string,
  isTranslations?: boolean,
  isImageTab?: boolean
) {
  const handlersRef = useRef<{
    onReady?: any;
    onPageCountReady?: any;
    onPageChanged?: any;
    onScalechanged?: any;
    onPageDisplayed?: any;
    onMarkCreated?: any;
    onMarkChanged?: any;
    onViewerClick?: any;
    onMarkRemoved?: any;
    onMarkSelectionChange?: any;
    onTextSelected?: any;
    onPageTextReady?: any;
  }>({});
  const [viewerControl, setViewerControl] = useState<ViewerControl | null>(
    null
  );
  const [markForRemark, setMarkForRemark] = useState<Mark | null>(null);
  const [viewer, setViewer] = useState<any>(null);
  const [thumbnailControl, setThumbnailControl] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [textSelection, setTextSelection] = useState<any>(null);
  const [markupLoaded, setMarkupLoaded] = useState<MarkUpLoaded | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const {
    user: {userId, userName, securityGroupId},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const [preRequisitesReady, setPreRequisitesReady] = useState(false);
  const [tools, setTools] = useState<{name: string; value: string}[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [initialScaleFactor, setInitialScaleFactor] = useState(1);
  const {data} = useGetViewingSession(documentparams, !!documentparams);
  const [selectedMark, setSelectedMark] = useState<SelectedMarkPosition | null>(
    null
  );
  const resizeHandlerRef = useRef<(() => void) | null>(null);
  const selectionModeRef = useRef('');
  const useAnnotationsRef = useRef(false);
  const sessionIdRef = useRef('');
  const annIdRef = useRef('');
  const translationRef = useRef(false);
  const imageTabRef = useRef(false);
  const multiPageMarksIdRef = useRef('');
  const currentRemarkColorRef = useRef(remarksColors[0]);
  const userDetailsRef = useRef({
    userId: 0,
    userName: '',
    securityGroupId: 0,
  });
  const annotationLayerIdRef = useRef('');
  const keywordsRef = useRef<DocViewKeyword[]>([]);
  const searchingRef = useRef<boolean>(false);
  const pendingPersistentSearchRef = useRef<boolean>(false);
  const [highlightSearchResults, setHighlightSearchResults] =
    useState<Record<string, ResultEntry>>();
  const [manualSearchResults, setManualSearchResults] = useState<
    SearchResult[]
  >([]);
  useEffect(() => {
    userDetailsRef.current = {userId, userName, securityGroupId};
  }, [userId, userName, securityGroupId]);
  useEffect(() => {
    selectionModeRef.current = selectionMode ?? '';
  }, [selectionMode]);
  useEffect(() => {
    sessionIdRef.current = sessionId ?? '';
  }, [sessionId]);
  useEffect(() => {
    useAnnotationsRef.current = !!useAnnotations;
  }, [useAnnotations]);
  useEffect(() => {
    annotationLayerIdRef.current = annotationLayerId ?? '';
  }, [annotationLayerId]);

  useEffect(() => {
    currentRemarkColorRef.current = currentRemarkColor ?? '';
  }, [currentRemarkColor]);

  useEffect(() => {
    keywordsRef.current = keywordsHighlight ?? [];
  }, [keywordsHighlight]);

  useEffect(() => {
    translationRef.current = isTranslations ?? false;
  }, [isTranslations]);
  useEffect(() => {
    imageTabRef.current = isImageTab ?? false;
  }, [isImageTab]);

  const searchDocument = useCallback(
    (vc: ViewerControl, searchTerm: string, isPersistentSearch = false) => {
      const uniqueBy = (arr: SearchQueryItem[], key: string) => {
        const seen = new Set();
        return arr.filter((item: any) => {
          if (seen.has(item[key])) return false;
          seen.add(item[key]);
          return true;
        });
      };
      const searchQueryArray =
        keywordsHighlight && !translationRef.current
          ? keywordsHighlight.flatMap((key) => {
              const terms = [];
              terms.push(
                ...(key?.keywords
                  ?.split(';')
                  .map((k) => k.trim().toLowerCase())
                  .filter(Boolean) ?? [])
              );

              return terms.map((term) => {
                if (key.isConcept) {
                  return {
                    highlightColor: `#${key.colorCode}`,
                    searchTerm: term,
                    searchTermIsRegex: true,
                    contextPadding: 25,
                  };
                } else
                  return {
                    highlightColor: `#${key.colorCode?.replace('#', '')}`,
                    searchTerm: term,
                    searchTermIsRegex: false,
                    contextPadding: 25,
                    matchingOptions: {
                      exactPhrase: true,
                      matchCase: false,
                      matchWholeWord: false,
                      beginsWith: false,
                      endsWith: false,
                      wildcard: false,
                    },
                  };
              });
            })
          : [];

      if (searchTerm) {
        searchQueryArray.push({
          highlightColor: searchHighlightColor,
          searchTerm: searchTerm.toLowerCase(),
          searchTermIsRegex: false,
          contextPadding: 25,
          matchingOptions: {
            exactPhrase: true,
            matchCase: false,
            matchWholeWord: false,
            beginsWith: false,
            endsWith: false,
            wildcard: false,
          },
        });
      }

      const query = {
        searchTerms: JSON.parse(
          JSON.stringify(uniqueBy(searchQueryArray, 'searchTerm'))
        ),
      };
      const runPendingPersistentSearch = () => {
        if (pendingPersistentSearchRef.current) {
          pendingPersistentSearchRef.current = false;
          // More page text became available while the previous search was
          // still running (e.g. later pages of a large document) — that
          // request was skipped, so re-run the persistent search now to
          // pick up terms on the newly-available pages.
          searchDocument(vc, '', true);
        }
      };

      if (
        !searchingRef.current &&
        searchQueryArray.length > 0 &&
        !imageTabRef.current &&
        (!translationRef.current || !isPersistentSearch)
      ) {
        searchingRef.current = true;
        setSearchLoading(true);

        try {
          const requestObject = vc.search(JSON.parse(JSON.stringify(query)));
          requestObject.on('SearchCompleted', function (_event) {
            let newResults = [];
            newResults = _event.completedSearchResults as SearchResult[];
            searchingRef.current = false;
            const searchMap = mapKeywordOccurrences(
              keywordsRef.current,
              newResults
            );
            setHighlightSearchResults(searchMap);
            if (searchTerm) {
              const manualResults: SearchResult[] = [];
              newResults.forEach((entry) => {
                const content = entry.text?.trim();
                if (!content) return;

                // Case-insensitive match (optional: remove .toLowerCase() if not wanted)
                const normalizedContent = normalizeString(
                  content.toLowerCase().trim()
                );

                if (
                  normalizeString(searchTerm.toLowerCase().trim()) ===
                  normalizedContent
                ) {
                  manualResults.push(entry);
                }
              });
              setManualSearchResults(manualResults);
            } else {
              setManualSearchResults([]);
            }
            setSearchLoading(false);
            runPendingPersistentSearch();
          });
          requestObject.on('SearchFailed', function () {
            searchingRef.current = false;
            setSearchLoading(false);
            runPendingPersistentSearch();
          });
          requestObject.on('SearchCancelled', function () {
            searchingRef.current = false;
            setSearchLoading(false);
            runPendingPersistentSearch();
          });
        } catch (err) {
          searchingRef.current = false;
          setSearchLoading(false);
          console.error('Failed to start document search:', err);
          runPendingPersistentSearch();
        }
      } else if (searchingRef.current && isPersistentSearch) {
        // A search is already running (e.g. triggered by an earlier page's
        // PageTextReady event) — remember to re-search once it finishes so
        // terms on this page aren't silently skipped.
        pendingPersistentSearchRef.current = true;
      }
    },
    []
  );

  // Step 1: create PAS viewing session using static file
  useEffect(() => {
    if (!documentparams || !data) return;
    setSessionId(data.viewingSessionId);
    annIdRef.current = `${documentparams.documentId}`;
  }, [documentparams, data]);

  useEffect(() => {
    const loadViewerPreRequisites = async () => {
      try {
        await Promise.all([
          injectScript(`${ViewerConfig.assetsBase}/js/viewercontrol.js`),
          injectScript(`${ViewerConfig.assetsBase}/js/viewerCustomizations.js`),
          injectScript(`${ViewerConfig.assetsBase}/js/jquery-3.7.1.min.js`),
          injectScript(`${ViewerConfig.assetsBase}/js/underscore.min.js`),
          injectCss(`${ViewerConfig.assetsBase}/css/viewer.css`),
          injectCss(`${ViewerConfig.assetsBase}/css/normalize.min.css`),
        ]);

        // Ordered loading
        await injectScript(
          `${ViewerConfig.assetsBase}/js/jquery.hotkeys.min.js`
        );
        await injectScript(`${ViewerConfig.assetsBase}/js/viewer.js`);
        setPreRequisitesReady(true);
      } catch (err) {
        // setPreReqError(`${err}`);
        console.log(err);
      }
    };

    loadViewerPreRequisites();
  }, []);

  // Step 2: initialize viewer + subscribe to events
  useEffect(() => {
    if (!sessionId || !preRequisitesReady || !containerRef.current) return;

    const PCC = (window as any).PCCViewer;
    if (!PCC) {
      console.error('PCCViewer is not available on window');
      return;
    }

    const el = document.getElementById('prizmViewer');
    if (!el) {
      console.error('Viewer element not found');
      return;
    }

    const vc: ViewerControl = PCC.ViewerControl(el, {
      el: containerRef.current,
      documentID: sessionId,
      annotationsMode: 'LayeredAnnotations',
      // template: exampleHelper.template,
      // language: window?.viewerCustomizations?.languages['en-US'],
      imageHandlerUrl: ViewerConfig.proxyBase,
      resourcePath: `${ViewerConfig.assetsBase}/img`,
      commentsPanelMode: 'full',
      // redactionReasons: exampleHelper.redactionReasons,
    });

    setViewerControl(vc);
    setViewer(PCC);

    const toolEnum = PCC.MouseTool.Type;
    const toolList = Object.keys(toolEnum).map((key) => ({
      name: key,
      value: toolEnum[key],
    }));
    setTools(toolList);
    // Handler: when viewer is ready
    const onReady = async () => {
      try {
        const thumbnailControlInstance = new (
          window as any
        ).PCCViewer.ThumbnailControl(document.getElementById('thumbnails'), vc);
        setThumbnailControl(thumbnailControlInstance);
        function syncControls(
          viewerControl: ViewerControl,
          thumbnailControl: ThumbnailControl
        ) {
          // scroll the document to the specified page whenever the user selects a new page
          thumbnailControl.on(
            window.PCCViewer.ThumbnailControl.EventType.PageSelectionChanged,
            function (ev) {
              viewerControl.setPageNumber(ev.pageNumbers[0]);
            }
          );

          // scroll the thumbnails as the user scrolls the document in the client
          // note: debounce the function to prevent unnecessary thumbnail
          // loading on fast scrolling
          viewerControl.on(
            window.PCCViewer.EventType.PageChanged,
            _.debounce(function (ev) {
              thumbnailControl.setSelectedPages(ev.pageNumber);
            }, 300)
          );

          // reflow thumbnails when the window resizes, in order to load
          // ones that may have come into view as a result, also refit the
          // client content
          const handleResize = _.debounce(function () {
            if (
              thumbnailControl &&
              typeof thumbnailControl?.reflow === 'function'
            )
              thumbnailControl?.reflow();

            // Remove this line if using the full client
            // viewerControl.fitContent(window.PCCViewer.FitType.FullWidth);
          }, DEBOUNCE_TIMER);
          resizeHandlerRef.current = handleResize;
          window.addEventListener('resize', handleResize);

          // set the selected page to the current page in the client
          thumbnailControl.setSelectedPages(viewerControl.getPageNumber());
        }
        syncControls(vc, thumbnailControlInstance);
        if (
          annIdRef.current &&
          useAnnotationsRef.current &&
          Boolean(Number(annotationLayerIdRef.current)) &&
          !translationRef.current &&
          !imageTabRef.current
        ) {
          vc.loadMarkup(annIdRef.current);
        }
        setViewerReady?.(true);
        setIsViewerReady(true);
      } catch (err) {
        console.error('Failed to fetch markup layers:', err);
      }
    };

    const onPageTextReady = () => {
      searchDocument(vc, '', true);
    };

    const onMarkCreated = (markup: any) => {
      let markId = markup.mark.getData('ID');
      if (!markId && markup.mark.type !== 'HighlightAnnotation') {
        const isMultiPageRedaction =
          selectionModeRef.current === 'MultiPageRedaction';
        markId = generateTimestampId(isMultiPageRedaction);
        markup.mark.setData(
          'securityGroupId',
          String(userDetailsRef.current.securityGroupId)
        );
        markup.mark.setData('userId', String(userDetailsRef.current.userId));
        markup.mark.setData(
          'userName',
          String(userDetailsRef.current.userName)
        );
        markup.mark.setData('ID', markId);
      }
      if (markup.mark.type === 'RectangleAnnotation') {
        // Rectangle Highlight - Auto Save
        markup.mark.setBorderThickness(1);
        markup.mark.setBorderColor('#FFFF00');
        markup.mark.setFillColor('#FFFF00');
        markup.mark.setOpacity(107);
        annIdRef.current &&
          vc.saveMarkup(
            `${annIdRef.current}-${markId}-${documentIdsToDisableControls.join(',')}-${annotationLayerIdRef.current}${connectionId ? `-${connectionId}` : ''}-RN`
          );
      }
      if (
        markup.mark.type === 'RectangleRedaction' &&
        selectionModeRef.current === MouseToolType.RectangleRedaction
      ) {
        //Rectangle Redaction - open popup for Redaction reason
        markup.mark.setBorderThickness(1);
        markup.mark.setBorderColor('#000000');
        markup.mark.setFillColor('#000000');
        setRedactionMarkId?.({
          redactionId: markId,
          id: markup.mark.getUid(),
          mark: markup.mark,
        });
      }
      if (
        markup.mark.type === 'TextSelectionRedaction' &&
        selectionModeRef.current === MouseToolType.TextSelectionRedaction
      ) {
        //Text Redaction - open popup for Redaction reason
        setRedactionMarkId?.({
          redactionId: markId,
          id: markup.mark.getUid(),
          mark: markup.mark,
        });
      }
      if (markup.mark.type === 'HighlightAnnotation') {
        vc.selectMarks([markup.mark]);
        markup.mark.setFillColor(
          `#${currentRemarkColorRef.current.replace('#', '')}`
        );
        vc.setCurrentMouseTool(MouseToolType.PanAndEdit);
        setMarkForRemark(markup.mark);
      }
    };

    const onMarkChanged = (markup: any) => {
      const markId = markup.mark.getData('ID');
      const markType = markup.mark.getType();
      const addedRedactionId = markup.mark.getData('RedactionTagID');
      const isLastPage = markup.mark.getData('IsLastPage');
      const isEditingRectangleAnnotation = markType === 'RectangleAnnotation';
      const isEditingTextRedaction =
        markType === 'TextSelectionRedaction' &&
        selectionModeRef.current !== MouseToolType.TextSelectionRedaction;
      const isEditingRectangleRedaction =
        markType === 'RectangleRedaction' &&
        selectionModeRef.current !== MouseToolType.RectangleRedaction &&
        selectionModeRef.current !== 'FullPageRedaction' &&
        selectionModeRef.current !== 'MultiPageRedaction';

      const isFullPageRdaction =
        markType === 'RectangleRedaction' &&
        selectionModeRef.current === 'FullPageRedaction';

      const isMultiPageRedaction =
        markType === 'RectangleRedaction' &&
        selectionModeRef.current === 'MultiPageRedaction';

      if (selectionModeRef.current === 'MultiPageRedaction') {
        const prevValue = multiPageMarksIdRef.current;
        multiPageMarksIdRef.current = prevValue
          ? `${prevValue},${markId}`
          : markId;
      }

      if (
        isEditingRectangleAnnotation ||
        isEditingRectangleRedaction ||
        isEditingTextRedaction
      ) {
        annIdRef.current &&
          vc.saveMarkup(
            `${annIdRef.current}-${markId}-${documentIdsToDisableControls.join(',')}-${annotationLayerIdRef.current}${connectionId ? `-${connectionId}` : ''}-E`
          );
      } else if (isFullPageRdaction && !!addedRedactionId) {
        const isCreate = markup.mark.getData('NewRedaction');
        if (isCreate) {
          markup.mark.setData('NewRedaction', undefined);
          annotationLayerIdRef.current &&
            vc
              .saveMarkup(
                `${annIdRef.current}-${markId}-${userDetailsRef.current.userId}-${redactionPropagationEnabled}-${addedRedactionId}-${annotationLayerIdRef.current}-${documentIdsToDisableControls?.join(',')}${connectionId ? `-${connectionId}` : ''}-N`
              )
              .then(() => {
                onRedactionSaved?.();
                triggerToast?.(
                  ToastType.Success,
                  t('docView.redationMenu.redactionSuccess')
                );
              });
        } else {
          annIdRef.current &&
            vc.saveMarkup(
              `${annIdRef.current}-${markId}-${documentIdsToDisableControls.join(',')}-${annotationLayerIdRef.current}${connectionId ? `-${connectionId}` : ''}-E`
            );
        }
      } else if (isMultiPageRedaction) {
        markup.mark.setData('NewRedaction', undefined);

        if (isLastPage) {
          markup.mark.setData('IsLastPage', undefined);
          annotationLayerIdRef.current &&
            vc
              .saveMarkup(
                `${annIdRef.current}-${multiPageMarksIdRef.current}-${userDetailsRef.current.userId}-${redactionPropagationEnabled}-${addedRedactionId}-${annotationLayerIdRef.current}-${documentIdsToDisableControls?.join(',')}${connectionId ? `-${connectionId}` : ''}-MN`
              )
              .then(() => {
                multiPageMarksIdRef.current = '';
                onRedactionSaved?.();
                triggerToast?.(
                  ToastType.Success,
                  t('docView.redationMenu.redactionSuccess')
                );
              });
        }
      }
    };
    // subscribe

    const onPageCountReady = (event: {pageCount: number}) => {
      if (event?.pageCount) setTotalPages(event.pageCount);
    };
    const onPageChanged = (event: {pageNumber: number}) => {
      if (event?.pageNumber) setPageNumber(event.pageNumber);
    };

    const onPageDisplayed = () => {
      setInitialScaleFactor(vc.getScaleFactor());
    };

    const onViewerClick = (event: ClickEvent) => {
      if (
        event.targetType === 'mark' &&
        vc.getCurrentMouseTool() === MouseToolType.PanAndEdit
      ) {
        const markComments = event.mark.getConversation()?.getComments() ?? [];
        if (markComments.length === 0) {
          // Prevent popup for Remarks
          setSelectedMark({
            x: event.clientX,
            y: event.clientY,
            mark: event.mark,
          });
        } else {
          vc.deselectMarks([event.mark]);
        }
      } else {
        setSelectedMark(null);
      }
    };

    const onMarkRemoved = (event: RemoveEvent) => {
      const markId = event.mark.getData('ID');
      if (markId) {
        annIdRef.current &&
          vc.saveMarkup(
            `${annIdRef.current}-${markId}-${documentIdsToDisableControls.join(',')}-${annotationLayerIdRef.current}${connectionId ? `-${connectionId}` : ''}-D`
          );
      }
      setSelectedMark(null);
    };

    const onMarkSelectionChange = () => {
      if (vc.getSelectedMarks()?.length === 0) {
        setSelectedMark(null);
      }
    };

    const onTextSelected = (event: any) => {
      setTextSelection(event);
    };

    // Removed Debounce to add multi page redaction
    // const debouncedMarkChanged = _.debounce(onMarkChanged, 300);

    const onMarkupLoaded = (event: MarkUpLoaded) => {
      setMarkupLoaded(event);
      setIsViewerReady(true);
    };

    vc.on(PCC.EventType.MarkSelectionChanged, onMarkSelectionChange);
    vc.on(PCC.EventType.ViewerReady, onReady);
    vc.on(PCC.EventType.PageChanged, onPageChanged);
    vc.on(PCC.EventType.PageCountReady, onPageCountReady);
    vc.on(PCC.EventType.MarkCreated, onMarkCreated);
    vc.on(PCC.EventType.MarkChanged, onMarkChanged);
    vc.on(PCC.EventType.Click, onViewerClick);
    vc.on(PCC.EventType.PageDisplayed, onPageDisplayed);
    vc.on(PCC.EventType.MarkRemoved, onMarkRemoved);
    vc.on(PCC.EventType.TextSelected, onTextSelected);
    vc.on(PCC.EventType.MarkupLoaded, onMarkupLoaded);
    vc.on(PCC.EventType.PageTextReady, onPageTextReady);

    handlersRef.current.onReady = onReady;
    handlersRef.current.onMarkSelectionChange = onMarkSelectionChange;
    handlersRef.current.onViewerClick = onViewerClick;
    handlersRef.current.onPageCountReady = onPageCountReady;
    handlersRef.current.onPageChanged = onPageChanged;
    handlersRef.current.onMarkCreated = onMarkCreated;
    handlersRef.current.onMarkRemoved = onMarkRemoved;
    handlersRef.current.onMarkChanged = onMarkChanged;
    handlersRef.current.onPageDisplayed = onPageDisplayed;
    handlersRef.current.onTextSelected = onTextSelected;
    handlersRef.current.onPageTextReady = onPageTextReady;

    // cleanup
    return () => {
      try {
        setViewerReady?.(false);
        setIsViewerReady(false);
        setTextSelection({});
        vc.off(PCC.EventType.MarkSelectionChanged, onMarkSelectionChange);
        vc.off(PCC.EventType.PageTextReady, onPageTextReady);

        vc.off(PCC.EventType.ViewerReady, handlersRef.current.onReady);
        vc.off(PCC.EventType.PageChanged, handlersRef.current.onPageChanged);
        vc.off(
          PCC.EventType.PageCountReady,
          handlersRef.current.onPageCountReady
        );
        vc.off(PCC.EventType.MarkCreated, onMarkCreated);
        vc.off(PCC.EventType.MarkChanged, onMarkChanged);
        vc.off(PCC.EventType.Click, onViewerClick);
        vc.off(PCC.EventType.PageDisplayed, onPageDisplayed);
        vc.off(PCC.EventType.MarkRemoved, onMarkRemoved);
        vc.off(PCC.EventType.TextSelected, onTextSelected);

        if (resizeHandlerRef.current) {
          window.removeEventListener('resize', resizeHandlerRef.current);
          resizeHandlerRef.current = null;
        }

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        if (typeof vc.destroy === 'function') {
          vc.destroy();
        }
        if (thumbContainerRef?.current) {
          thumbContainerRef.current.innerHTML = '';
        }
        
        if (rightPanelRef?.current) {
          rightPanelRef.current.style.display = 'none';
        }
        
        if (userDetailsRef?.current?.userId !== 0) {
          userDetailsRef.current = {
            userId: 0,
            userName: '',
            securityGroupId: 0,
          };
        }
        if (sessionIdRef?.current) {
          sessionIdRef.current = '';
        }
      } catch (e) {
        console.warn('Viewer cleanup error', e);
      }
    };
  }, [
    sessionId,
    containerRef,
    preRequisitesReady,
    annIdRef,
    thumbContainerRef,
    useAnnotations,
    setViewerReady,
    annotationLayerIdRef,
    connectionId,
    userDetailsRef,
    selectionModeRef,
    multiPageMarksIdRef,
    keywordsRef,
    searchingRef,
    onRedactionSaved,
  ]);

  return {
    viewerControl,
    thumbnailControl,
    sessionId,
    tools,
    viewer,
    totalPages,
    pageNumber,
    initialScaleFactor,
    isViewerReady,
    selectedMark,
    setSelectedMark,
    textSelection,
    markupLoaded,
    highlightSearchResults,
    markForRemark,
    setMarkForRemark,
    manualSearchResults,
    searchDocument,
    searchLoading,
  };
}
